import { useDarkMode } from '../DarkModeContext';
import useIsVisible from '../isVisible';
import { useRef } from 'react';

import ShotScan from '../pic/WhatsApp Image 2026-06-10 at 11.30.05.jpeg';
import ShotFound from '../pic/WhatsApp Image 2026-06-10 at 11.30.05 (1).jpeg';
import ShotConnecting from '../pic/WhatsApp Image 2026-06-10 at 11.30.05 (4).jpeg';
import ShotConnected from '../pic/WhatsApp Image 2026-06-10 at 11.30.05 (2).jpeg';
import ShotSaved from '../pic/WhatsApp Image 2026-06-10 at 11.30.05 (3).jpeg';

const screenshots = [
  { src: ShotScan, caption: 'Bereit zum Scannen' },
  { src: ShotFound, caption: 'Gerät gefunden' },
  { src: ShotConnecting, caption: 'Verbindungsaufbau' },
  { src: ShotConnected, caption: 'Verbunden & konfigurierbar' },
  { src: ShotSaved, caption: 'Einstellung gespeichert' },
];

const Phone = ({ src, caption, isDarkMode }) => (
  <figure className="flex flex-col items-center shrink-0">
    <div className={`rounded-[2rem] p-2 border shadow-xl ${
      isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-900 border-gray-800'
    }`}>
      <img
        src={src}
        alt={caption}
        className="w-44 sm:w-48 rounded-[1.5rem] block"
      />
    </div>
    <figcaption className={`mt-3 text-xs font-medium text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
      {caption}
    </figcaption>
  </figure>
);

const Chip = ({ text, dark }) => (
  <span className={`inline-block px-3 py-1 text-xs rounded-full border mr-2 mb-2 font-mono ${
    dark
      ? 'bg-Template-1/20 border-Template-2/40 text-Template-2'
      : 'bg-Template-0/10 border-Template-1/40 text-Template-0'
  }`}>{text}</span>
);

function AppPage() {
  const { isDarkMode } = useDarkMode();

  const refs = [useRef(), useRef(), useRef(), useRef()];
  const vis = [
    useIsVisible(refs[0]),
    useIsVisible(refs[1]),
    useIsVisible(refs[2]),
    useIsVisible(refs[3]),
  ];

  const sec = (i) => `px-8 py-12 transition-opacity ease-in duration-700 ${vis[i] ? 'opacity-100' : 'opacity-0'} ${
    i % 2 === 0
      ? isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
      : isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
  }`;

  const h2 = `text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-Template-0'}`;
  const hr = `border-t-2 my-4 ${isDarkMode ? 'border-gray-600' : 'border-Template-2'}`;
  const body = `text-base leading-relaxed mb-4 max-w-3xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const li = `leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <>
      <div ref={refs[0]} className={sec(0)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Überblick</h2>
          <hr className={hr} />
          <p className={body}>
            Die SmartDice-App läuft auf <strong>iOS</strong> und verbindet sich per{' '}
            <strong>Bluetooth Low Energy (BLE 5.0)</strong> direkt mit dem Würfel. Nach dem Pairing
            zeigt die App das aktuelle Würfelergebnis in Echtzeit an und erlaubt die Konfiguration
            aller Würfel-Parameter – ohne Neustart oder Firmware-Update.
          </p>
          <div className="flex flex-wrap">
            <Chip text="iOS" dark={isDarkMode} />
            <Chip text="BLE 5.0" dark={isDarkMode} />
            <Chip text="GATT" dark={isDarkMode} />
            <Chip text="Flutter" dark={isDarkMode} />
          </div>
        </div>
      </div>

      <div ref={refs[1]} className={sec(1)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Design</h2>
          <hr className={hr} />
          <p className={body}>
            Die App besteht aus zwei Hauptscreens: dem <strong>Connect-Screen</strong>, auf dem
            verfügbare SmartDice-Geräte gescannt und verbunden werden, und dem{' '}
            <strong>Dice-Screen</strong>, der das Würfelergebnis groß anzeigt und Konfigurationsoptionen
            bereitstellt.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 max-w-xl">
            {[
              {
                screen: 'Connect Screen',
                desc: 'BLE-Scan nach SmartDice-Geräten in der Nähe. Auswahl und Verbindungsaufbau. Reconnect nach Verbindungsverlust.',
              },
              {
                screen: 'Dice Screen',
                desc: 'Großes Würfelergebnis in Echtzeit. Konfigurationsmenü für Farbe, Würfeltyp und Modus. Würfelhistorie.',
              },
            ].map((s) => (
              <div key={s.screen} className={`rounded-xl border p-4 ${
                isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-Template-2 shadow-sm'
              }`}>
                <div className={`font-bold mb-2 text-sm ${isDarkMode ? 'text-Template-2' : 'text-Template-0'}`}>{s.screen}</div>
                <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div ref={refs[2]} className={sec(2)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Verwendung</h2>
          <hr className={hr} />
          <p className={body}>
            Nach der Verbindung kann der Würfel vollständig über die App personalisiert werden.
            Jede Einstellung wird direkt per BLE auf den Würfel übertragen und sofort wirksam.
          </p>
          <ul className={`list-disc pl-6 space-y-2 text-sm`}>
            <li className={li}><strong>Farbe:</strong> LED-Farbe pro Würfelergebnis frei wählbar (Einzel- oder 3-Farben-Modus)</li>
            <li className={li}><strong>Würfeltyp:</strong> D20, D12, D10, D8, D6, D4 – virtuell auswählbar</li>
            <li className={li}><strong>Reconnect:</strong> App verbindet sich automatisch wieder nach Verbindungsunterbrechung</li>
            <li className={li}><strong>BLE Test:</strong> Direkte Test-Ansicht für Entwicklung und Debugging</li>
          </ul>
        </div>
      </div>

      <div ref={refs[3]} className={sec(3)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Screenshots</h2>
          <hr className={hr} />
          <p className={body}>
            Vom BLE-Scan bis zur fertigen Konfiguration – ein Blick in die App.
          </p>
          <div className="flex gap-6 overflow-x-auto pb-4 -mx-2 px-2 sm:justify-center sm:flex-wrap">
            {screenshots.map((s) => (
              <Phone key={s.caption} {...s} isDarkMode={isDarkMode} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default AppPage;
